<template>
  <div class="w-full flex justify-center waiting-list-configuration-templates">
    <div class="w-full lg:w-4/5 xl:w-2/3 max-w-3xl py-base">
      <vs-tabs
        v-if="templatesLoaded"
        v-model="activeTab"
        position="left"
        color="primary"
        class="flex items-center"
      >
        <vs-tab
          :label="$t('FromFieldName')"
          icon-pack="feather"
          icon="icon-phone"
        >
          <div class="vx-row">
            <div class="vx-col w-full">
              <label class="font-bold vs-input--label">
                <span class="inline-flex">
                  <span class="mb-base">
                    {{ $t('PickYourSenderTitle') }}
                  </span>

                  <vx-tooltip
                    :text="$t('PickYourSenderMsg')"
                    position="top"
                    class="inline-block ml-2">
                    <feather-icon
                      icon="HelpCircleIcon"
                      svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
                  </vx-tooltip>
                </span>
              </label>
            </div>

            <div class="vx-col w-full">
              <campaign-information-from
                v-model="senderId"
                ref="campaignInformationFrom"
                :caller-id-type.sync="callerIdType"
                :compact-design="true"
                :sender-business-name="senderBusinessName"
                :field-name="$t('FromFieldName')"
                :show-label="false"
                :append-to-body="true"
                class="mt-3"
                @is-valid="(val) => senderIdIsValid = val"
              />
            </div>
          </div>
        </vs-tab>

        <vs-tab
          :label="$t('InitialQueuePositionTabTitle')"
          icon-pack="feather"
          icon="icon-hash"
        >
        <p class="font-bold mb-base">{{ $t('CustomQueueNumberingTitle') }}</p>
          <div class="vx-row">
            <div class="vx-col w-full">
              <label class="vs-input--label required">
                <span class="inline-flex">
                  <span>
                    {{ $t('InitialQueuePositionLabel') }}
                  </span>

                  <vx-tooltip
                    :text="$t('InitialQueuePositionMsg')"
                    position="top"
                    class="inline-block ml-2">
                    <feather-icon
                      icon="HelpCircleIcon"
                      svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
                  </vx-tooltip>
                </span>
              </label>
            </div>

            <div class="vx-col w-full">
              <vs-input
                v-model.number="initialQueuePosition"
                class="w-full sm:w-2/5 xl:w-1/2"
                type="number"
                :name="$t('InitialQueuePositionLabel')"
                :placeholder="initialQueuePosition === 0 ? '' : $t('InitialQueuePositionPlaceholder')"
                v-validate="'required|integer|min_value:0'"
                data-vv-validate-on="input|blur|change"
                icon="icon icon-hash"
                icon-pack="feather"
                :danger="errors.has($t('InitialQueuePositionLabel'))"
                :danger-text="errors.first($t('InitialQueuePositionLabel'))"
                val-icon-danger="clear"/>
            </div>
          </div>

          <div class="vx-row mt-5">
            <div class="vx-col w-full">
              <label class="vs-input--label">
                <span class="inline-flex">
                  <span>
                    {{ $t('PrefixQueuePositionLabel') }}
                  </span>

                  <vx-tooltip
                    :text="$t('PrefixQueuePositionMsg')"
                    position="top"
                    class="inline-block ml-2">
                    <feather-icon
                      icon="HelpCircleIcon"
                      svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
                  </vx-tooltip>
                </span>
              </label>
            </div>

            <div class="vx-col w-full">
              <vs-input
                :value="prefixQueuePosition"
                class="w-full sm:w-2/5 xl:w-1/2"
                type="text"
                :name="$t('PrefixQueuePositionLabel')"
                :placeholder="$t('PrefixQueuePositionPlaceholder')"
                v-validate="'alpha_num'"
                maxlength="3"
                data-vv-validate-on="input|blur|change"
                icon="icon icon-type"
                icon-pack="feather"
                :danger="errors.has($t('PrefixQueuePositionLabel'))"
                :danger-text="errors.first($t('PrefixQueuePositionLabel'))"
                val-icon-danger="clear"
                @input="prefixQueuePositionUpperCase($event)"
              />
            </div>
          </div>

          <div
            v-if="initialQueuePosition === 0 || initialQueuePosition"
            class="vx-row mt-base">
            <div class="vx-col w-full">
              <p >
                {{ $t('PrefixQueuePositionExample', {
                    turns: turnsExampleText,
                  })
                }}
              </p>
            </div>
          </div>
        </vs-tab>

        <vs-tab
          v-for="(template, key, index) in templates"
          :key="key"
          :label="$t(`$${key}.Title`)"
          icon-pack="feather"
          :icon="template.icon"
        >
          <label class="font-bold">
            <span class="inline-flex">
              <span class="mb-base">
                {{ $t(`$${key}.Title`) }}
              </span>

              <vx-tooltip
                position="top"
                class="inline-block ml-2">
                <template #inner>
                  <div v-html="$t(`$${key}.Msg`)"></div>
                </template>
                <feather-icon
                  icon="HelpCircleIcon"
                  svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
              </vx-tooltip>
            </span>
          </label>

          <sms-text-box
            :ref="`smsTextBox${index}`"
            v-model="template.message"
            :has-interpolations.sync="template.hasInterpolations"
            :interpolations.sync="template.interpolations"
            :is-waiting-list-message="true"
            class="w-full mt-3"
            @state-change="(state) => template.isValid = state"
            @form-was-changed="(val) => template.wasChanged = val">
          </sms-text-box>

          <vs-button
            color="dark"
            type="border"
            size="small"
            class="mt-3"
            @click="confirmGenerateTemplate(key)">
            <feather-icon icon="RefreshCcwIcon" svgClasses="h-4 w-4" class="mr-2" />
            {{ $t('GenerateFromTemplate') }}
          </vs-button>
        </vs-tab>
      </vs-tabs>

      <div class="flex justify-end mt-5">
        <vs-button
          v-if="showUpdate"
          :disabled="!formWasChanged"
          @click="saveInternal"
        >
          {{ $t('$General.Update') }}
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import SmsTextBox from '@/views/modules/_components/SMSTextBox.vue';
import VxTooltip from '@/layouts/components/vx-tooltip/VxTooltip.vue';
import smsTemplateService from '@/api/modules/sms-template.service';
import CampaignInformationFrom from '@/views/modules/campaigns/components/campaign-information/CampaignInformationFrom.vue';

export default {
  name: 'WaitingListConfigurationTemplates',
  i18n: {
    messages: {
      en: {
        FromFieldName: 'Sender number',
        PickYourSenderTitle: 'Pick your sender number',
        PickYourSenderMsg: 'Select the phone number you want to use to send messages.',

        InitialQueuePositionTabTitle: 'Initial queue position',
        CustomQueueNumberingTitle: 'Custom queue numbering',

        InitialQueuePositionLabel: 'Starting turn number',
        InitialQueuePositionPlaceholder: '123',
        InitialQueuePositionMsg: 'Set the number your queue will start from. Useful for resuming previous sessions or syncing multiple service points. (e.g., if you set 120, customers will receive 120, 121, 122, etc.)',

        PrefixQueuePositionLabel: 'Turn prefix (optional)',
        PrefixQueuePositionPlaceholder: 'AB3',
        PrefixQueuePositionMsg: 'Add an optional alphanumeric prefix to organise and distinguish queues or services. (e.g., if you set prefix A and start at 1, customers will see A-1, A-2, A-3, etc.)',
        PrefixQueuePositionExample: 'Turns will appear as: {turns}, etc.',

        CustomiseYourMessagesTitle: 'Customise your messages',
        CustomiseYourMessagesMsg: 'Set up messages for key events like confirmations, cancellations, and notifications. These will be sent automatically during the queue attendance process and can be updated anytime.',

        $queueConfirmationSmsTemplate: {
          Title: 'Queue confirmation',
          Msg: "Clients receive a confirmation when they join the queue. <br/><br/>It confirms their place and shows their position and the current turn, helping set clear expectations.",
        },
        $almostReadySmsTemplate: {
          Title: 'Notification text',
          Msg: "Send when a client's turn is approaching. <br/><br/>Prompt them to be ready so the flow stays smooth and delays are minimised.",
        },
        $itsYourTurnSmsTemplate: {
          Title: 'Your turn notification',
          Msg: "Send when you're ready to attend to the client. <br/><br/>Invite them to come forward with clear, timely instructions.",
        },
        $thankYouReviewRequestSmsTemplate: {
          Title: 'Completion and feedback request',
          Msg: 'After the service, thank the client and share a link to leave a review. <br/><br/>You can also include any final reminders or recommendations.',
        },
        $cancellationTurnSmsTemplate: {
          Title: 'Cancellation notification',
          Msg: 'Send promptly if you need to cancel a client\'s turn. <br/><br/>Explain the situation and offer next steps (e.g. how to rejoin) to maintain trust.',
        },
        $UpdatedWaitingListTemplatesNtf: {
          Title: 'Waitlist messaging',
          Msg: 'Waitlist messaging updated successfully.',
        },
        GenerateFromTemplate: 'Restore to default',
        $GenerateFromTemplateConfirm: {
          Title: 'Restore {templateName} message?',
          Msg: 'This action will restore the message to its default content. Any changes you made will be lost.',
        },
      },
    },
  },
  components: {
    CampaignInformationFrom,
    VxTooltip,
    SmsTextBox,
  },
  props: {
    tabIndex: {
      type: Number,
      required: true,
    },
    showUpdate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: this.tabIndex,
      isValidCurrentTab: false,
      templates: {},
      defaultTemplates: {
        queueConfirmationSmsTemplate: {
          name: 'Waitlist - Queue confirmation',
          message: `Hi [${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}], you're in the queue at [${this.$enums.Campaign.BusinessPlaceholders.BUSINESS_NAME}] turn [${this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER}]. Now you have [${this.$enums.Campaign.WaitListPlaceholders.SPOTS_AHEAD}] spots ahead. We'll text you when you're up!`,
          hasInterpolations: true,
          interpolations: [
            {
              shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}]`,
              attribute: this.$enums.Campaign.DefaultPlaceholdersAttributeName.FIRST_NAME,
              type: this.$enums.Attributes.Type.DEFAULT,
              name: this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME,
            },
            {
              shorthand: `[${this.$enums.Campaign.BusinessPlaceholders.BUSINESS_NAME}]`,
              attribute: this.$enums.Campaign.BusinessPlaceholders.BUSINESS_NAME,
              type: this.$enums.Attributes.Type.BUSINESS,
              name: this.$enums.Campaign.BusinessPlaceholders.BUSINESS_NAME,
            },
            {
              shorthand: `[${this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER}]`,
              attribute: this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER,
              type: this.$enums.Attributes.Type.WAITLIST,
              name: this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER,
            },
            {
              shorthand: `[${this.$enums.Campaign.WaitListPlaceholders.SPOTS_AHEAD}]`,
              attribute: this.$enums.Campaign.WaitListPlaceholders.SPOTS_AHEAD,
              type: this.$enums.Attributes.Type.WAITLIST,
              name: this.$enums.Campaign.WaitListPlaceholders.SPOTS_AHEAD,
            },
          ],
          isValid: true,
          wasChanged: false,
          step: 1,
          waitingListMessage: this.$enums.WaitingList.MESSAGE.QUEUE_CONFIRMATION,
          icon: 'icon-users',
        },
        almostReadySmsTemplate: {
          name: 'Waitlist - Notification text',
          message: `Hi [${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}], we're almost ready for you at [${this.$enums.Campaign.BusinessPlaceholders.BUSINESS_NAME}]! Please be prepared.`,
          hasInterpolations: true,
          interpolations: [
            {
              shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}]`,
              attribute: this.$enums.Campaign.DefaultPlaceholdersAttributeName.FIRST_NAME,
              type: this.$enums.Attributes.Type.DEFAULT,
              name: this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME,
            },
            {
              shorthand: `[${this.$enums.Campaign.BusinessPlaceholders.BUSINESS_NAME}]`,
              attribute: this.$enums.Campaign.BusinessPlaceholders.BUSINESS_NAME,
              type: this.$enums.Attributes.Type.BUSINESS,
              name: this.$enums.Campaign.BusinessPlaceholders.BUSINESS_NAME,
            },
          ],
          isValid: true,
          wasChanged: false,
          step: 2,
          waitingListMessage: this.$enums.WaitingList.MESSAGE.ALMOST_READY,
          icon: 'icon-bell',
        },
        itsYourTurnSmsTemplate: {
          name: "Waitlist - It's your turn",
          message: `Hi [${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}], it's your turn now. Please come forward, and we'll get started right away. Thanks for waiting!`,
          hasInterpolations: true,
          interpolations: [
            {
              shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}]`,
              attribute: this.$enums.Campaign.DefaultPlaceholdersAttributeName.FIRST_NAME,
              type: this.$enums.Attributes.Type.DEFAULT,
              name: this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME,
            },
          ],
          isValid: true,
          wasChanged: false,
          step: 3,
          waitingListMessage: this.$enums.WaitingList.MESSAGE.ITS_YOUR_TURN,
          icon: 'icon-user-check',
        },
        thankYouReviewRequestSmsTemplate: {
          name: 'Waitlist - Thank you & review request',
          message: `Hi [${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}], thanks for visiting us! We'd love your feedback. Click to rate your experience: [${this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK}]`,

          hasInterpolations: true,
          interpolations: [
            {
              shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}]`,
              attribute: this.$enums.Campaign.DefaultPlaceholdersAttributeName.FIRST_NAME,
              type: this.$enums.Attributes.Type.DEFAULT,
              name: this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME,
            },
            {
              shorthand: `[${this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK}]`,
              attribute: this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK,
              type: this.$enums.Attributes.Type.SPECIAL,
              name: this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK,
            },
          ],
          isValid: true,
          wasChanged: false,
          step: 4,
          waitingListMessage: this.$enums.WaitingList.MESSAGE.THANKS_AND_REVIEW,
          icon: 'icon-star',
        },
        cancellationTurnSmsTemplate: {
          name: 'Waitlist - Cancellation notification',
          message: `Your turn [${this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER}] on [${this.$enums.Campaign.WaitListPlaceholders.TURN_DATE}] has been cancelled. We hope to see you again soon.`,
          hasInterpolations: true,
          interpolations: [
            {
              shorthand: `[${this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER}]`,
              attribute: this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER,
              type: this.$enums.Attributes.Type.WAITLIST,
              name: this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER,
            },
            {
              shorthand: `[${this.$enums.Campaign.WaitListPlaceholders.TURN_DATE}]`,
              attribute: this.$enums.Campaign.WaitListPlaceholders.TURN_DATE,
              type: this.$enums.Attributes.Type.WAITLIST,
              name: this.$enums.Campaign.WaitListPlaceholders.TURN_DATE,
            },
          ],
          isValid: true,
          wasChanged: false,
          step: 5,
          waitingListMessage: this.$enums.WaitingList.MESSAGE.CANCELLATION_TURN,
          icon: 'icon-x',
        },
      },

      templatesLoaded: false,

      callerIdType: this.$enums.Sender.Caller.Type.SHARED_NUMBER,
      senderId: '',
      senderBusinessName: '',
      senderIdIsValid: false,

      initialQueuePosition: 1,
      prefixQueuePosition: '',
      fieldsWereChangedFlag: false,
    };
  },
  computed: {
    ...mapGetters({
      shortReviewLink: 'auth/tenantShortReviewLink',
      getTenantFlag: 'auth/getTenantFlag',
      tenantAccountSettingsWaitingList: 'auth/tenantAccountSettingsWaitingList',
    }),
    turnsExampleText() {
      if (this.initialQueuePosition !== 0 && !this.initialQueuePosition) {
        return '';
      }

      const prefixSeparator = this.prefixQueuePosition
        ? '-'
        : '';

      return [0, 1, 2].map((number) => `${this.prefixQueuePosition}${prefixSeparator}${this.initialQueuePosition + number}`).join(', ');
    },
    verifyProperties() {
      return Object.values(this.templates).every(
        (template) => template.message.trim() !== '' && template.isValid,
      );
    },
    someTemplateWasChanged() {
      return Object.values(this.templates).some(
        (template) => template.wasChanged,
      );
    },
    senderWasChanged() {
      return this.callerIdType !== this.tenantAccountSettingsWaitingList.sender.callerIdType
        || (!!this.senderId && !this.tenantAccountSettingsWaitingList.sender.senderId)
        || (!!this.senderId && !!this.tenantAccountSettingsWaitingList.sender.senderId
        && typeof this.senderId === 'object' && this.senderId.id !== this.tenantAccountSettingsWaitingList.sender.senderId
        );
    },
    fieldsWereChanged() {
      const fields = this.validationFields;
      return Object.values(fields).some((field) => field.changed || field.dirty);
    },
    formWasChanged() {
      return this.fieldsWereChangedFlag
        || this.someTemplateWasChanged
        || this.senderWasChanged;
    },
    toSaveTemplates() {
      return Object.fromEntries(
        Object.entries(this.templates).map(([key, template]) => [
          key,
          this.removeKeys(template, ['isValid', 'wasChanged', 'icon']),
        ]),
      );
    },
  },
  async created() {
    this.callerIdType = this.tenantAccountSettingsWaitingList.sender.callerIdType
      || this.$enums.Sender.Caller.Type.SHARED_NUMBER;
    this.senderId = this.tenantAccountSettingsWaitingList.sender.senderId || '';
    this.senderBusinessName = this.tenantAccountSettingsWaitingList.sender.senderBusinessName || '';

    this.initialQueuePosition = this.tenantAccountSettingsWaitingList.initialQueuePosition || 1;
    this.prefixQueuePosition = this.tenantAccountSettingsWaitingList.prefixQueuePosition || '';

    this.$vs.loading({ type: 'radius' });
    const { data } = await this.fetchWaitingListTemplates();

    this.templates = {
      ...Object.fromEntries(
        Object.entries(this.defaultTemplates).map(([key, template]) => [
          key,
          this.getMergedSavesTemplate(template, data),
        ]),
      ),
    };

    this.templatesLoaded = true;
    this.$vs.loading.close();
  },
  mounted() {
    document.addEventListener('click', this.handleClickTabItem);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickTabItem);
  },
  watch: {
    fieldsWereChanged(newVal) {
      if (newVal) {
        this.fieldsWereChangedFlag = true;
      }
    },
    async tabIndex(newVal, prevVal) {
      if (newVal < prevVal) {
        this.activeTab = newVal;
      } else {
        const isValid = await this.validateStep(prevVal);

        if (isValid) {
          this.activeTab = newVal;
        } else {
          this.$emit('update:tabIndex', prevVal);
        }
      }
    },
  },
  methods: {
    ...mapActions({
      updateTenantAccountWaitingListSettings: 'auth/updateTenantAccountWaitingListSettings',
    }),
    async fetchWaitingListTemplates() {
      const response = await smsTemplateService.getAll({
        filters: {
          waitingListMessage: {
            filterType: this.$enums.AppFilterType.ENUM,
            type: this.$enums.AppFilter.FilterType.Enum.Type.ONE,
            filter: [
              this.$enums.WaitingList.MESSAGE.QUEUE_CONFIRMATION,
              this.$enums.WaitingList.MESSAGE.ALMOST_READY,
              this.$enums.WaitingList.MESSAGE.ITS_YOUR_TURN,
              this.$enums.WaitingList.MESSAGE.THANKS_AND_REVIEW,
              this.$enums.WaitingList.MESSAGE.CANCELLATION_TURN,
            ],
          },
        },
      });

      return response.data;
    },
    removeKeys(object, keysToRemove) {
      return Object.fromEntries(
        Object.entries(object).filter(([key]) => !keysToRemove.includes(key)),
      );
    },
    getMergedSavesTemplate(template, savedTemplates) {
      const foundTemplate = savedTemplates.find(
        (savedTemplate) => savedTemplate.waitingListMessage === template.waitingListMessage,
      );

      if (foundTemplate) {
        return {
          ...template,
          name: foundTemplate.name,
          message: foundTemplate.message,
          hasInterpolations: foundTemplate.hasInterpolations,
          interpolations: foundTemplate.interpolations,
          wasChanged: false,
        };
      }

      return template;
    },
    handleClickTabItem(event) {
      const tabs = document.querySelectorAll('.waiting-list-configuration-templates .vs-tabs--ul .vs-tabs--li');

      const tabIndex = Array.from(tabs).indexOf(event.target);

      if (tabIndex !== -1) {
        this.$emit('update:tabIndex', tabIndex);
      }
    },
    async validateStep(step) {
      if (step === 0) {
        return this.$refs.campaignInformationFrom.validate();
      }

      if (step === 1) {
        return this.$validator.validateAll();
      }

      await this.$refs[`smsTextBox${step - 2}`][0].$validator.validateAll();
      return this.$refs[`smsTextBox${step - 2}`][0].validate();
    },
    async saveInternal() {
      const isValid = await this.validateStep(this.activeTab);

      if (isValid) {
        await this.saveTemplates();
      }
    },
    async saveTemplates() {
      if (this.verifyProperties && (this.formWasChanged || !this.getTenantFlag('completedWaitingListSettings'))) {
        this.$vs.loading({ type: 'radius' });
        await this.updateTenantAccountWaitingListSettings({
          ...this.toSaveTemplates,
          sender: {
            callerIdType: this.callerIdType,
            senderId: this.senderId ? this.senderId.id : null,
            senderBusinessName: this.senderBusinessName,
          },
          initialQueuePosition: this.initialQueuePosition,
          prefixQueuePosition: this.prefixQueuePosition,
        });
        this.templates = {
          ...Object.fromEntries(
            Object.entries(this.templates).map(([key, template]) => [
              key,
              {
                ...template,
                wasChanged: false,
                isValid: true,
              },
            ]),
          ),
        };
        this.$vs.loading.close();

        this.$showSuccessActionNotification({
          title: this.$t('$UpdatedWaitingListTemplatesNtf.Title'),
          text: this.$t('$UpdatedWaitingListTemplatesNtf.Msg'),
        });
      }

      return this.verifyProperties;
    },
    prefixQueuePositionUpperCase(value) {
      this.prefixQueuePosition = value.toUpperCase().trim();
    },
    confirmGenerateTemplate(key) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('$GenerateFromTemplateConfirm.Title', {
          templateName: this.$t(`$${key}.Title`).toLowerCase(),
        }),
        text: this.$t('$GenerateFromTemplateConfirm.Msg'),
        accept: () => this.generateTemplate(key),
      });
    },
    generateTemplate(key) {
      const defaultTemplate = this.defaultTemplates[key];
      const target = this.templates[key];

      target.message = defaultTemplate.message;
      target.hasInterpolations = defaultTemplate.hasInterpolations;
      target.interpolations = JSON.parse(JSON.stringify(defaultTemplate.interpolations));
      target.isValid = true;
      target.wasChanged = true;
    },
  },
};
</script>

<style lang="scss">
.waiting-list-configuration-templates {
  .con-vs-tabs.vs-tabs {
    .con-ul-tabs {
      //pointer-events: none;
    }

    .vs-tabs--li {
      cursor: pointer;

      span {
        display: none;
      }

      .vs-tabs--btn {
        pointer-events: none;
      }
    }

    .con-slot-tabs {
      margin-left: 24px; /* Default for all screen sizes */
    }
  }
}

@media (min-width: 768px) {
  .waiting-list-configuration-templates {
    .con-vs-tabs.vs-tabs {
      .vs-tabs--li {
        span {
          display: inline-block;
        }
      }

      .con-slot-tabs {
        margin-left: 32px; /* Larger margin for screens 768px and wider */
      }
    }
  }
}
</style>
